import React from 'react';
import { Cog, Edit2, Layers, Plus, RefreshCw, Trash2 } from 'lucide-react';

import { SelectField } from '@/components/phone/FieldControls';
import { CardAction, ChipRow, EmptyState, PageHeader, PhonePage, PillButton, SearchBar, StatusPill } from '@/components/phone/primitives';
import { Colors } from '@/components/phone/theme';

/**
 * The stage registry, on the admin's phone.
 *
 * The desktop is a six-column grid grouped by category. Here each category is
 * a heading and each stage under it a card: what it is called, what it is on
 * the floor, whether it can go out to a vendor, and whether it is live — with
 * the same three actions the row offers.
 */

const STATUS_CHIPS = [
  { key: 'all', label: 'All status' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
];

function StageCard({ stage, dotClass, onMachines, onEdit, onDelete }) {
  const inactive = stage.is_active === false;
  return (
    <li
      data-testid={`stage-card-${stage.id}`}
      className="mt-2 rounded-[10px] border"
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
    >
      <div className="px-4 pt-3">
        <p className="text-[15px] font-bold -tracking-[0.2px]" style={{ color: inactive ? Colors.text.tertiary : Colors.text.primary }}>
          {stage.name || stage.stage_name}
        </p>
        {!!stage.notes && (
          <p className="mt-0.5 text-[12px]" style={{ color: Colors.text.secondary }}>{stage.notes}</p>
        )}
        <p className="mt-0.5 truncate font-mono text-[11px]" style={{ color: Colors.text.tertiary }}>
          {stage.slug || stage.stage_key || '—'}
        </p>
        <div className="flex flex-wrap items-center gap-2 py-3">
          <span className="inline-flex items-center gap-1.5 text-[12px] font-medium" style={{ color: Colors.text.secondary }}>
            <span className={`h-2 w-2 rounded-full ${dotClass}`} />
            {stage.category}
          </span>
          {stage.supports_external_vendor && <StatusPill label="Can go out" tone="caution" />}
          <StatusPill label={inactive ? 'Inactive' : 'Active'} tone={inactive ? 'neutral' : 'positive'} dot />
        </div>
      </div>
      <div className="flex items-center gap-2 border-t px-3 py-2" style={{ borderColor: Colors.border.subtle }}>
        <CardAction icon={Cog} label="Machines" onClick={onMachines} testID={`stage-machines-${stage.id}`} />
        <CardAction icon={Edit2} label="Edit" onClick={onEdit} testID={`stage-edit-${stage.id}`} />
        <CardAction icon={Trash2} label="Delete" tone="critical" onClick={onDelete} className="ml-auto" testID={`stage-delete-${stage.id}`} />
      </div>
    </li>
  );
}

export default function PhoneProductionStages({
  stages, stagesByCategory, categories, categoryFilter, activeFilter, dotClassFor,
  onSearch, onCategory, onActive, onAdd, onRefresh, onMachines, onEdit, onDelete,
}) {
  const groups = Object.entries(stagesByCategory);
  const shown = groups.reduce((n, [, list]) => n + list.length, 0);

  return (
    <PhonePage testID="production-stages-page">
      <PageHeader
        title="Production Stages"
        subtitle={`${stages.length} the floor can run`}
        action={(
          <PillButton onClick={onAdd} testID="add-stage-btn">
            <Plus className="h-4 w-4" /> Add
          </PillButton>
        )}
      />

      <div className="px-5">
        <SearchBar placeholder="Search stages…" onChange={onSearch} testID="stages-search-input" />
        <div className="mt-3">
          <SelectField
            name="category"
            label="Category"
            value={categoryFilter}
            options={[{ value: 'all', label: 'All categories' }, ...categories.map((c) => ({ value: c, label: c }))]}
            onChange={onCategory}
          />
        </div>
        <div className="mt-2">
          <ChipRow chips={STATUS_CHIPS} active={activeFilter} onSelect={onActive} testIDPrefix="status" />
        </div>
      </div>

      <div className="flex-1 px-5 pb-8">
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[12px] font-medium" style={{ color: Colors.text.secondary }}>{shown} shown</span>
          <button
            type="button"
            onClick={onRefresh}
            className="flex h-10 items-center gap-1 text-[12px] font-semibold"
            style={{ color: Colors.accent.DEFAULT }}
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
        {shown === 0 ? (
          <EmptyState icon={Layers} message="No stages found" hint="Try another filter, or add the first one." />
        ) : groups.map(([category, list]) => (
          <section key={category} className="mt-3" data-testid={`stage-group-${category}`}>
            <h2 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider" style={{ color: Colors.text.secondary }}>
              <span className={`h-2 w-2 rounded-full ${dotClassFor(category)}`} />
              {category}
              <span className="font-normal normal-case tracking-normal" style={{ color: Colors.text.tertiary }}>
                {list.length} stage{list.length === 1 ? '' : 's'}
              </span>
            </h2>
            <ul>
              {list.map((stage) => (
                <StageCard
                  key={stage.id}
                  stage={stage}
                  dotClass={dotClassFor(stage.category)}
                  onMachines={() => onMachines(stage)}
                  onEdit={() => onEdit(stage)}
                  onDelete={() => onDelete(stage)}
                />
              ))}
            </ul>
          </section>
        ))}
      </div>
    </PhonePage>
  );
}
